/**
 * BeatPlayer — drains a queue of director Beats onto a Figure.
 *
 * The player is the only thing that calls figure.playClipFromUrl while
 * the director is active. The manual dropdown is disabled when the
 * director is running.
 *
 * ── Sequencing rules ──────────────────────────────────────────────────────
 *
 * Every beat is one-shot. The body clip plays once with `LoopOnce +
 * clampWhenFinished`, then the figure clamps on the last frame while the
 * face / breath / holds / state layers continue.
 *
 * Advance happens at `max(clip natural duration, beat.duration_sec)` —
 * `duration_sec` is a FLOOR. Whichever signal comes second triggers
 * `advance()`:
 *   - clip-finished: fired by `figure.setOnClipFinished` when the LoopOnce
 *     action plays through.
 *   - timer-fired:   set if `beat.duration_sec` is non-null and > 0.
 *
 * If `duration_sec` is null and there's no body clip (face_only), the
 * timer is the only signal. If `duration_sec` is null and there IS a clip,
 * clip-finished alone advances (zero dwell).
 *
 * Empty queue: cycle the idle variant pool indefinitely until more beats
 * arrive via `enqueue`.
 *
 * ── Variety mechanics ──────────────────────────────────────────────────────
 *
 * - Time-scale jitter: each clip plays at 0.9–1.1× speed (uniform random).
 *   The breathing layer is unaffected — it has its own timeScale driven by
 *   BPM, not the figure's global `setTimeScale`.
 *
 * - Opportunistic mirroring: for beats marked `symmetric`, the figure's
 *   root is flipped on the X axis ~50% of the time. This doubles the
 *   apparent variety without any extra assets. The flip is reset between
 *   beats so it doesn't accumulate.
 *
 * ── Thread model ──────────────────────────────────────────────────────────
 *
 * All logic runs on the JS event loop (no web workers). Timers use
 * `setTimeout` with the beat duration; one-shots use a callback wired
 * via `figure.setOnClipFinished`. Only one timer / callback is active
 * at a time; `stop()` clears everything.
 */

import type { Figure } from '../motion/figure';
import { PROCEDURAL_HOLD_LIMBS, type LimbGroup } from '../motion/body-profiles';
import type { EasingFamily } from '../motion/easing';
import type { Beat } from './types';
import { tempoForEmotion } from './emotion-tempo';
import type { Emotion } from './vocab';

// Limb claims for GLB-backed holds (matches the server-side table in
// holds.ts). Both Uthana hold types currently target the right arm.
const GLB_HOLD_LIMBS: Record<string, LimbGroup[]> = {
  hand_on_chin: ['right_arm'],
  hand_over_mouth: ['right_arm'],
};

function holdLimbs(name: string): LimbGroup[] {
  if (name in PROCEDURAL_HOLD_LIMBS) {
    return PROCEDURAL_HOLD_LIMBS[name as keyof typeof PROCEDURAL_HOLD_LIMBS];
  }
  return GLB_HOLD_LIMBS[name] ?? [];
}

// ── Crossfade durations ───────────────────────────────────────────────────
//
// Beat-to-beat crossfades have to bridge two clips that may have completely
// different rest poses (every Uthana clip starts in its own near-A-pose).
// A short fade *is* the visible "beat" the user sees: the body snaps from
// where clip A ended toward clip B's neutral start.
//
// We use two durations:
//   POSE_CROSSFADE_SEC — used for any pose-to-pose transition. The default.
//                        Long enough that the body settles between poses.
//   LOCO_CROSSFADE_SEC — used only when both beats are uthana_locomotion
//                        sharing the same gait phase; here we *want* the
//                        fade to be quick so the cycle continues cleanly.
const POSE_CROSSFADE_SEC = 0.5;
const LOCO_CROSSFADE_SEC = 0.4;
/** Crossfade into the fallback idle when the queue empties. */
const FALLBACK_IDLE_FADE_SEC = 1.1;
/**
 * Random walk on the global time scale so back-to-back beats with the same
 * energy don't pulse in lock-step. Because timeScale is now lerped (see
 * figure.ts), this jitter is gentle — it shifts the *target*, not the
 * applied value, so the body's apparent tempo never jumps.
 */
const JITTER = 0.1;
/**
 * Clips whose names match this pattern are choreographed to a fixed tempo;
 * energy-driven time-scale is bypassed so they play at their authored speed.
 */
const DANCE_RE = /\bdanc(e|ing)\b|\bsamba\b|\bbellydance\b|\bbreakdanc/i;

// ── Isolation toggles ─────────────────────────────────────────────────────
//
// Set each to false to disable that layer entirely. Use these to isolate
// which layer is conflicting with HY-Motion animations. Re-enable one by
// one until the problem reappears.
//
/** Additive breathing overlay on spine/chest/shoulders. */
const BREATHING_ENABLED = false;
/** Procedural body gestures (weight_shift, glance_over_shoulder, etc.). */
const BODY_GESTURES_ENABLED = false;
/** Additive hold poses (arms_crossed, hand_on_hip, etc.). */
const HOLDS_ENABLED = false;
/** Energy-driven clip time scale (0.6–1.4× based on emotion). When false, all clips play at 1×. */
const ENERGY_TIMESCALE_ENABLED = false;
/**
 * Fraction of symmetric clips that get L/R mirror treatment.
 * Disabled (0) by default: opportunistic mirror only reads cleanly with
 * motion-matched assets, which we don't have. The flip teleports the
 * figure root scale, contributing to the "visible beat" feeling. We keep
 * the code in place behind this constant so it can be re-enabled later.
 */
const MIRROR_PROB = 0;

// ── Emotion → ARKit blendshape presets ────────────────────────────────────
//
// Maps the catalog emotion vocabulary to sets of ARKit 52 shape-key weights.
// Values are tuned for Lewis (Avaturn GLB). Characters without morph targets
// silently ignore these — setExpression() is a no-op on FBX characters.
//
// Intensities are kept moderate (~0.4–0.8) so expressions read clearly
// without tipping into caricature. The render tick lerps between presets
// over ~0.5 s, so transitions are always smooth even on rapid beat changes.

export const EMOTION_PRESETS: Record<string, Record<string, number>> = {
  neutral:    {},
  calm:       { browInnerUp: 0.08 },
  bored:      { eyeBlinkLeft: 0.25, eyeBlinkRight: 0.25, mouthFrownLeft: 0.15, mouthFrownRight: 0.15 },
  tired:      { eyeBlinkLeft: 0.4, eyeBlinkRight: 0.4, mouthFrownLeft: 0.2, mouthFrownRight: 0.2, browInnerUp: 0.2 },
  joy:        { mouthSmileLeft: 0.65, mouthSmileRight: 0.65, cheekSquintLeft: 0.3, cheekSquintRight: 0.3 },
  excitement: { mouthSmileLeft: 0.85, mouthSmileRight: 0.85, cheekSquintLeft: 0.4, cheekSquintRight: 0.4, browOuterUpLeft: 0.4, browOuterUpRight: 0.4 },
  triumphant: { mouthSmileLeft: 0.75, mouthSmileRight: 0.75, cheekSquintLeft: 0.35, cheekSquintRight: 0.35, browOuterUpLeft: 0.3, browOuterUpRight: 0.3 },
  sadness:    { mouthFrownLeft: 0.6, mouthFrownRight: 0.6, browInnerUp: 0.65, browDownLeft: 0.15, browDownRight: 0.15 },
  defeated:   { mouthFrownLeft: 0.45, mouthFrownRight: 0.45, browInnerUp: 0.4, eyeSquintLeft: 0.2, eyeSquintRight: 0.2 },
  anger:      { browDownLeft: 0.75, browDownRight: 0.75, noseSneerLeft: 0.35, noseSneerRight: 0.35, mouthPressLeft: 0.45, mouthPressRight: 0.45 },
  disgust:    { noseSneerLeft: 0.65, noseSneerRight: 0.65, mouthUpperUpLeft: 0.35, browDownLeft: 0.3, browDownRight: 0.3 },
  fear:       { browOuterUpLeft: 0.65, browOuterUpRight: 0.65, browInnerUp: 0.5, eyeWideLeft: 0.55, eyeWideRight: 0.55, mouthFrownLeft: 0.25, mouthFrownRight: 0.25 },
  tense:      { browDownLeft: 0.35, browDownRight: 0.35, browInnerUp: 0.25, mouthPressLeft: 0.35, mouthPressRight: 0.35 },
  surprise:   { jawOpen: 0.45, browOuterUpLeft: 0.75, browOuterUpRight: 0.75, eyeWideLeft: 0.65, eyeWideRight: 0.65 },
  disbelief:  { browOuterUpLeft: 0.5, browOuterUpRight: 0.5, eyeWideLeft: 0.4, eyeWideRight: 0.4, mouthLeft: 0.25 },
  confused:   { browInnerUp: 0.3, browDownLeft: 0.35, eyeSquintRight: 0.2, mouthLeft: 0.2 },
};

export interface BeatPlayerCallbacks {
  /** Fired when a beat starts playing. Use to track history. */
  onBeatStart?: (beat: Beat) => void;
  /**
   * Fired when the queue drops to this depth or below (default 1).
   * The director client uses this to trigger a `/direct/continue` refill.
   */
  onQueueLow?: (depth: number) => void;
  /** Depth at which `onQueueLow` fires (default 1). */
  queueLowThreshold?: number;
  /** Fired when the player state changes — useful for the debug overlay. */
  onStateChange?: (state: BeatPlayerState) => void;
  /**
   * Fired when a beat has a pre-generated vocalization audio URL.
   * The caller is responsible for fetching and playing the audio with
   * real-time mouth sync. The `offsetSec` is the delay (in seconds)
   * after the beat starts before audio should begin.
   */
  onVocalization?: (url: string, offsetSec: number) => void;
}

export interface BeatPlayerState {
  current: Beat | null;
  queueDepth: number;
  inGap: boolean;
  /** Total seconds playing since start() was first called. */
  elapsedSec: number;
  mirrored: boolean;
}

export class BeatPlayer {
  private queue: Beat[] = [];
  private current: Beat | null = null;
  private figure: Figure;
  private defaultIdleUrl: string;
  private callbacks: BeatPlayerCallbacks;
  private readonly clipFinishedHandler: () => void;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private mirrored = false;
  private startMs = 0;
  private beatStartMs = 0;
  private lastTimeScale = 1;
  private active = false;
  /**
   * Two-signal advance state for the current beat. The beat advances when
   * BOTH flags are true (or when only one is meaningful, e.g. face_only
   * has no clip-finished signal so timerFired alone advances).
   *
   * Set fresh on every playBeat() call.
   */
  private timerFired = false;
  private clipFinished = false;
  /** True when the current beat doesn't expect a clip-finished signal. */
  private clipFinishedNotExpected = false;

  // ── Idle variant pool ──────────────────────────────────────────────────
  // Library URLs cycled when the queue is empty. Cross-faded between every
  // 6–12 s so the character never settles into a single visible loop —
  // and each variant is played as a LoopPingPong so even back-to-back
  // repetitions look different in each direction.
  private idlePool: string[] = [];
  private idlePoolIdx = -1;
  private idleSwapTimer: ReturnType<typeof setTimeout> | null = null;
  /** Min seconds before a new idle variant crossfades in. */
  private static IDLE_MIN_SEC = 6;
  /** Max seconds before a new idle variant crossfades in. */
  private static IDLE_MAX_SEC = 12;

  // ── Active hold tracking ───────────────────────────────────────────────
  // Holds persist across beats, but their *effective* additive weight is
  // re-applied every beat as a function of that beat's energy (see
  // applyHoldsForBeat). Holds are an additive layer that fights the primary
  // body clip — at high energy (locomotion, fist pumps) we attenuate the
  // hold so the kinetic motion reads cleanly; at low energy (sad, tired,
  // contemplative) the hold is at full base weight.
  //
  // The figure tracks the AnimationActions internally; we just store the
  // (url, baseWeight) pair so we can re-issue setHold() with a fresh
  // effective weight each beat.
  private activeHolds = new Map<
    string,
    {
      url: string;
      baseWeight: number;
      attackSec?: number;
      /**
       * Easing family captured from the activating beat's emotion+intensity.
       * Stored alongside the hold so we can re-issue setHold each beat
       * with a stable curve. The family on a sustaining hold is
       * intentionally NOT updated by subsequent beats' emotion changes —
       * that would re-shape the curve mid-flight. The family changes only
       * when a hold is explicitly released and re-activated.
       */
      easing?: EasingFamily;
    }
  >();

  constructor(
    figure: Figure,
    defaultIdleUrl: string,
    callbacks: BeatPlayerCallbacks = {},
  ) {
    this.figure = figure;
    this.defaultIdleUrl = defaultIdleUrl;
    this.callbacks = callbacks;

    // Clip-finished signal. Used as one of the two advance triggers
    // (the other is the duration_sec timer). Whichever fires LAST
    // actually advances — duration_sec is a floor.
    this.clipFinishedHandler = () => {
      if (!this.active) return;
      if (!this.current) return;
      this.clipFinished = true;

      // Log actual beat duration for diagnostics (especially useful for
      // locomotion beats where we need to verify strides × cycle time).
      const actualMs = this.beatStartMs > 0 ? Date.now() - this.beatStartMs : 0;
      const b = this.current;
      console.log(
        `[beat-player] clipFinished: "${b.clip.slice(0, 40)}" source=${b.source} ` +
        `strides=${(b as { strides?: number }).strides ?? 'n/a'} ` +
        `timeScale=${this.lastTimeScale.toFixed(2)} ` +
        `actual_duration=${(actualMs / 1000).toFixed(2)}s`,
      );

      // Loopable clips (e.g. bored idles, breathing walks) are designed as
      // seamless cycles — their last exported frame is mid-motion, not a
      // neutral rest pose. Freezing there with clampWhenFinished produces the
      // "stuck arms at 45°" artifact. Always crossfade back to the default
      // idle when the clip ends, regardless of whether the timer has already
      // fired. If the timer fired first, the advance happens immediately after
      // the crossfade starts (arms smoothly dropping during the next beat).
      if (this.current.loopable || this.current.source === 'uthana_text') {
        this.figure
          .playClipFromUrl(this.defaultIdleUrl, 2.5, true, true)
          .catch((err) =>
            console.warn('[beat-player] loopable idle-recovery failed:', err),
          );
      }

      this.maybeAdvance();
    };
    figure.setOnClipFinished(this.clipFinishedHandler);
  }

  /**
   * Configure the pool of idle clip URLs the fallback / gap layer cycles
   * through. Called from main.ts after the manifest loads. Empty pool falls
   * back to the single defaultIdleUrl.
   */
  setIdlePool(urls: string[]): void {
    this.idlePool = urls.filter(Boolean);
    this.idlePoolIdx = -1;
  }

  /**
   * Append beats to the queue. If nothing is currently playing, start
   * draining immediately.
   */
  enqueue(beats: Beat[]): void {
    this.queue.push(...beats);
    this.notifyQueueDepth();
    if (this.active && !this.current) {
      this.advance();
    }
  }

  /** Replace the default idle URL (called after character swap). */
  setDefaultIdle(url: string): void {
    this.defaultIdleUrl = url;
  }

  /** Start the player. Resets elapsed time. Begins draining the queue. */
  start(): void {
    this.active = true;
    this.startMs = Date.now();
    this.advance();
  }

  /** Pause playback and clear all pending timers. The queue is preserved. */
  stop(): void {
    this.active = false;
    this.clearTimer();
    this.clearIdleSwapTimer();
    this.current = null;
    this.timerFired = false;
    this.clipFinished = false;
    this.clipFinishedNotExpected = false;
    this.resetMirror();
    this.figure.setTimeScale(1);
    // Release any additive holds and clear our tracking. A subsequent
    // start() begins from a clean state; if the LLM wants the same hold
    // back, the next beat's `holds` array will reactivate it.
    this.figure.releaseAllHolds();
    this.activeHolds.clear();
    // Reset eye/brow rates to neutral so a new session doesn't inherit
    // the anxious/excited rates from a previous performance.
    this.figure.setEmotionState('neutral', 0.6, 0.5);
    // Release any director-scripted gaze override so the next session
    // doesn't start with the eyes pinned to the last beat's direction.
    this.figure.setGazeOverride(null);
    // Drop sustained body posture back to neutral so a new performance
    // doesn't inherit "slumped_idle" or "tense_idle" from the last one.
    this.figure.setBodyState('neutral_idle');
    this.emitState();
  }

  /** Hot-swap the figure after a character change. All internal state is
   *  kept; subsequent calls will target the new figure immediately. */
  setFigure(f: Figure): void {
    this.figure = f;
    f.setOnClipFinished(this.clipFinishedHandler);
  }

  /** Flush the queue and stop. */
  reset(): void {
    this.queue = [];
    this.stop();
  }

  get queueDepth(): number {
    return this.queue.length;
  }

  get elapsedSec(): number {
    if (this.startMs === 0) return 0;
    return (Date.now() - this.startMs) / 1000;
  }

  get state(): BeatPlayerState {
    return {
      current: this.current,
      queueDepth: this.queue.length,
      inGap: false, // gap mechanism removed; field kept for HUD compat.
      elapsedSec: this.elapsedSec,
      mirrored: this.mirrored,
    };
  }

  /**
   * Names of all currently-active additive hold layers (e.g. `hand_on_chin`).
   * The hold layer survives across beats and is re-weighted by each new beat's
   * energy via `applyHoldsForBeat`, so this list reflects the layers the
   * figure is *actually* blending right now (not just the holds the latest
   * beat's `holds` field requested). Emptied by `stop()` and `reset()`.
   */
  get activeHoldNames(): string[] {
    return [...this.activeHolds.keys()];
  }

  // ── Private ──────────────────────────────────────────────────────────────

  /**
   * Drain the next beat from the queue. Called from start(), enqueue(),
   * and from `maybeAdvance()` when the active beat's advance signals
   * (clip-finished and/or duration timer) have all fired.
   *
   * The old auto-insert idle-gap mechanism is gone: dwell time is now
   * authored on each beat via `duration_sec`, so directors get the same
   * effect with finer control and no infinite-gap-loop foot-gun.
   */
  private advance(): void {
    if (!this.active) return;

    if (this.queue.length === 0) {
      this.notifyQueueDepth();
      this.playFallbackIdle();
      return;
    }

    const beat = this.queue.shift()!;
    this.playBeat(beat);
    this.notifyQueueDepth();
  }

  /**
   * Advance only when both the duration timer (if any) and the clip-
   * finished event (if any) have fired. This is what gives `duration_sec`
   * its FLOOR semantics — the beat lasts at least the timer, and at
   * least the natural clip duration, whichever is longer.
   *
   * The two flags are pre-seeded inside playBeat():
   *   - timerFired = true       when no duration_sec was set (no timer scheduled)
   *   - clipFinishedNotExpected = true  when the beat has no body clip
   *                                     (face_only) so no `finished` event
   *                                     will ever fire.
   *
   * That way both signals always converge to "true" and the beat advances
   * exactly once, on whichever real signal arrives last.
   */
  private maybeAdvance(): void {
    if (!this.active || !this.current) return;
    const clipDone = this.clipFinished || this.clipFinishedNotExpected;
    const timerDone = this.timerFired;
    if (clipDone && timerDone) {
      this.advance();
    }
  }

  private playBeat(beat: Beat): void {
    // Capture the previous beat *before* we swap — needed by chooseFadeSec
    // so we can pick a short fade for locomotion-to-locomotion transitions
    // and a longer one for everything else.
    const previous = this.current;
    this.current = beat;
    this.beatStartMs = Date.now();
    this.clearTimer();
    this.clearIdleSwapTimer();
    // Reset the two-signal advance state for this new beat.
    this.timerFired = false;
    this.clipFinished = false;
    this.clipFinishedNotExpected = false;

    // ── Energy → global tempo + small jitter ──────────────────────────────
    // energy 0.2 → ~0.75× speed (sluggish/depressed)
    // energy 0.5 → ~1.00× speed (neutral)
    // energy 0.8 → ~1.25× speed (rushed)
    // energy 1.0 → ~1.40× speed (panicked)
    // A small ±5% jitter on top so back-to-back beats with the same energy
    // don't pulse in lock-step.
    //
    // Dance clips are excluded: they're choreographed to a specific tempo
    // and look absurd when energy pushes them to 1.3-1.4×.
    const isDance = DANCE_RE.test(beat.clip);
    const energy = beat.energy ?? 0.5;
    const energyScale = isDance ? 1 : 0.6 + energy * 0.8; // 0.6 .. 1.4
    const jitter = 1 + (Math.random() - 0.5) * JITTER; // ±5%
    const appliedTimeScale = ENERGY_TIMESCALE_ENABLED ? energyScale * jitter : 1.0;
    this.lastTimeScale = appliedTimeScale;
    this.figure.setTimeScale(appliedTimeScale);

    // face_only beats inherit the previous mirror/clip — don't flip the
    // figure root (the body isn't changing and a sudden mirror would look
    // like a teleport).
    const isFaceOnly = beat.source === 'face_only';
    if (!isFaceOnly) this.applyMirror(beat);

    // Breath envelope — applies to face_only too. Breath is the most
    // important continuity signal when the body is static.
    if (BREATHING_ENABLED && beat.breath) {
      this.figure
        .setBreathing(beat.breath)
        .catch((err) => console.warn('[beat-player] setBreathing failed', err));
    }

    // ── Facial expression — preset × intensity ────────────────────────────
    // Intensity multiplies the preset weights so the same emotion can read
    // as a flicker (0.3) or a peak reaction (1.0). Falls back to 0.6 if
    // the LLM omitted the field.
    const intensity = beat.intensity ?? 0.6;
    const basePreset = EMOTION_PRESETS[beat.emotion] ?? EMOTION_PRESETS['neutral']!;
    const scaledPreset: Record<string, number> = {};
    for (const [shape, weight] of Object.entries(basePreset)) {
      scaledPreset[shape] = weight * intensity;
    }
    // Diagnostic: log what we're applying so we can verify the LLM is sending
    // proper emotion/intensity values rather than defaulting to neutral.
    const bodyFields: string[] = [];
    if (beat.body_state) bodyFields.push(`body_state=${beat.body_state}`);
    if (beat.body_gesture) bodyFields.push(`body_gesture=${beat.body_gesture}`);
    if (beat.gesture) bodyFields.push(`gesture=${beat.gesture}`);
    const bodyTrail = bodyFields.length > 0 ? ` ${bodyFields.join(' ')}` : '';
    console.log(
      `[beat-player] beat "${beat.clip.slice(0, 40)}" source=${beat.source} emotion=${beat.emotion} intensity=${intensity.toFixed(2)} energy=${(beat.energy ?? 0.5).toFixed(2)}${bodyTrail} → preset shapes: [${Object.keys(scaledPreset).join(', ') || '(none — falls back to ambient)'}]`,
    );
    this.figure.setExpression(scaledPreset);

    // Emotion-driven eye/brow rates — anxious blinks faster, sad looks down,
    // surprised barely blinks, etc. The figure picks an EyeProfile per
    // emotion and adjusts blink/saccade/brow scheduler intervals + amplitudes
    // to match. Energy multiplies all interval rates (high arousal = faster).
    this.figure.setEmotionState(beat.emotion, intensity, beat.energy ?? 0.5);

    // Director-scripted gaze. When the LLM specified a `gaze` field, lock
    // the eyes to that target for the duration of this beat (procedural
    // saccades are suspended). When omitted/null, release any prior
    // override and let the saccades take over again. We pass intensity as
    // the gaze amplitude scale so a "soft" beat doesn't lock with a hard,
    // staring gaze.
    this.figure.setGazeOverride(beat.gaze ?? null, intensity);

    // Procedural gesture — a short envelope (300 ms – 1.5 s) overlaying the
    // current emotion to capture transients (gasp impact, laugh convulsions).
    // The figure ignores null/undefined and unknown names; gestures auto-
    // clear when their duration expires, so we fire-and-forget here.
    if (beat.gesture) {
      this.figure.triggerGesture(beat.gesture);
    }
    if (BODY_GESTURES_ENABLED && beat.body_gesture) {
      this.figure.triggerBodyGesture(beat.body_gesture);
    }
    // ── body_state is currently DISABLED ─────────────────────────────────
    // Procedural body-state biases (slumped_idle / tense_idle /
    // confident_idle / restless_idle) are layered on TOP of any active
    // hold via the same bone-offset accumulator. In practice the bias
    // and the hold target the same shoulder/spine bones simultaneously
    // and the additive math drives the arms through the torso —
    // visually broken. Until the layering is fixed (proper masking so
    // body_state only biases bones not claimed by an active hold), we
    // ignore beat.body_state entirely. The LLM is also told not to emit
    // the field (see director.ts), but defending here keeps stale
    // history from re-triggering it.
    void beat.body_state;

    // ── Holds: auto-release before locomotion ────────────────────────────
    // Walking with arms_crossed / hands_on_hips / etc. produces visible
    // arm-clipping because the locomotion clip's arm motion is
    // suppressed by the additive hold layer — the figure walks with
    // limbs that look glued in place. Whenever the next beat is
    // uthana_locomotion, drop every currently-active hold first so the
    // walk reads cleanly. The LLM is told to release holds before
    // locomotion explicitly, but enforcing it client-side is the
    // belt-and-braces version.
    if (beat.source === 'uthana_locomotion' && this.activeHolds.size > 0) {
      const dropped = [...this.activeHolds.keys()];
      for (const name of dropped) {
        this.figure.releaseHold(name);
      }
      this.activeHolds.clear();
      console.log(
        `[beat-player] auto-released ${dropped.length} hold(s) before locomotion: ${dropped.join(', ')}`,
      );
    }

    // Holds — release, activate, and re-apply weights to all currently
    if (HOLDS_ENABLED) {
      this.applyHoldsForBeat(beat);
    }

    this.callbacks.onBeatStart?.(beat);

    // ── Vocalization audio ────────────────────────────────────────────────
    // If the server pre-generated audio for this beat, fire the callback so
    // the caller can play it through the mouth-sync pipeline. The offset_sec
    // delay (e.g. 0.3s for a yawn that builds before the jaw opens) is passed
    // through verbatim — the caller schedules the actual AudioContext start.
    if (beat.vocalization_url) {
      const offsetSec = beat.vocalization?.offset_sec ?? 0;
      this.callbacks.onVocalization?.(beat.vocalization_url, offsetSec);
    }

    // ── face_only fast-path ────────────────────────────────────────────────
    // No body clip change is *required*. The point of face_only is that the
    // emotional layer (face, breath, holds) carries the moment while the
    // body holds still.
    //
    // EXCEPTION: if the previous beat was a locomotion clip, the body
    // mixer is currently clamped on the LAST FRAME of that walk —
    // typically a mid-stride pose with one foot lifted. Holding that for
    // a face_only dwell looks broken ("character paused mid-walk").
    // Crossfade back to the default idle so the figure smoothly returns
    // to a standing pose at its NEW world position before the face_only
    // runs. We still don't await the swap — the face/breath/hold layers
    // run in parallel and the duration timer alone gates advance.
    //
    // For non-locomotion previous beats (library / uthana_text / face_only)
    // the existing "freeze on last frame" semantics still apply because
    // those clips end on poses that are intended to linger (e.g. arms
    // crossed, hand on chin).
    //
    // The advance signal is the duration timer alone (no clip-finished
    // event will fire because we don't load a new clip). We seed
    // clipFinishedNotExpected = true so maybeAdvance() ignores that side.
    if (isFaceOnly) {
      if (previous?.source === 'uthana_locomotion') {
        // Crossfade to idle to recover from the mid-stride clamp. Use a
        // generous 1.5 s — a full sprint leaves arms pumping and legs
        // fully extended; 0.5 s snapped the body back too abruptly.
        this.figure
          .playClipFromUrl(this.defaultIdleUrl, 1.5, true, true)
          .catch((err) => {
            console.warn(
              `[beat-player] post-locomotion idle recovery failed (continuing):`,
              err,
            );
          });
      }
      this.clipFinishedNotExpected = true;
      this.scheduleDurationTimer(beat);
      this.emitState();
      return;
    }

    // ── Post-locomotion idle recovery for library beats ───────────────────
    // NOTE: do NOT fire a simultaneous idle recovery here. figure.ts tracks
    // `currentAction` and updates it synchronously — calling playClipFromUrl
    // twice in the same tick results in three concurrent AnimationActions
    // (sprint + idle + library) whose weights are not auto-normalised by
    // Three.js, causing the weights to sum above 1.0 and bone positions to
    // overshoot/flicker. The longer chooseFadeSec (2.0 s for loco→library)
    // handles the deceleration blend in a single crossfade instead.

    const fadeSec = BeatPlayer.chooseFadeSec(previous, beat);
    // Locomotion beats keep the hip-position track so the figure
    // actually translates through world space; library beats play on a
    // treadmill (lockInPlace = true). The camera follow in main.ts
    // smooth-tracks the figure's hip world position so a 2-stride spurt
    // pans the camera with the character instead of leaving them off
    // frame.
    //
    // HY-Motion gestures (uthana_text) are a third path: lockInPlace is
    // still true (we don't want them to walk away through world space)
    // BUT preserveHip is true so the *frame-0-anchored* hip-position
    // track survives. Without this, lockClipInPlace strips the track
    // entirely and lean / reach / crouch get forced into spine rotation
    // alone, producing the bent-over / hands-clipping artefact reported
    // in earlier sessions. The frame-0 anchor (added in figure.ts's
    // fetchRawClip) keeps the character's feet on the floor at clip
    // start; only the deltas from frame 0 are expressed through the
    // hip — same trick the cc-base path already uses for Riya / Anne.
    const isLocomotion = beat.source === 'uthana_locomotion';
    const isHyMotion = beat.source === 'uthana_text';
    const lockInPlace = !isLocomotion;
    const preserveHip = isHyMotion;
    const strides = isLocomotion ? Math.max(1, beat.strides ?? 1) : 1;
    if (isLocomotion) {
      const expectedDuration = strides * 0.5 / this.lastTimeScale; // rough estimate
      console.log(
        `[beat-player] LOCOMOTION beat — clip="${beat.clip}" strides=${strides} ` +
        `energy=${(beat.energy ?? 0.5).toFixed(2)} timeScale=${this.lastTimeScale.toFixed(2)} ` +
        `expected≈${expectedDuration.toFixed(1)}s duration_sec=${beat.duration_sec}`,
      );
    }
    this.figure
      // All beats are one-shot. Clip plays through, clamps on the last
      // frame, and we wait for clip-finished + duration timer (whichever
      // is later) before advancing. For locomotion, the hip track is
      // stitched to strides×naturalDuration so the figure walks the full
      // distance without freezing mid-stride.
      .playClipFromUrl(beat.url, fadeSec, false, lockInPlace, strides, preserveHip)
      .then(() => {
        if (!this.active) return;
        this.scheduleDurationTimer(beat);
      })
      .catch((err) => {
        console.warn(
          `[beat-player] playClipFromUrl failed for "${beat.clip}" (${beat.url}):`,
          err,
          '— skipping beat',
        );
        // Skip to next beat rather than stalling
        this.advance();
      });

    this.emitState();
  }

  /**
   * Schedule the duration_sec timer for the current beat. If the beat
   * has no duration_sec (or zero/negative), seed timerFired = true
   * immediately so the advance is gated only by the clip-finished signal.
   */
  private scheduleDurationTimer(beat: Beat): void {
    const dur = beat.duration_sec;
    if (typeof dur !== 'number' || !Number.isFinite(dur) || dur <= 0) {
      // No timer requirement — let clip-finished alone drive advance.
      this.timerFired = true;
      // Re-check: if clip-finished already arrived, advance now.
      this.maybeAdvance();
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.active) return;
      this.timerFired = true;
      this.maybeAdvance();
    }, dur * 1000);
  }

  private playFallbackIdle(): void {
    // Queue is empty — cycle through the idle variant pool indefinitely
    // with crossfade + ping-pong so the result never reads as a hard loop.
    // When enqueue() is called again with new beats, advance() will be
    // triggered from there and will crossfade out of the current variant.
    this.current = null;
    this.resetMirror();
    this.figure.setTimeScale(1);

    const url = this.pickNextIdleUrl();
    this.figure
      .playClipFromUrl(url, FALLBACK_IDLE_FADE_SEC, 'pingpong')
      .catch((err) =>
        console.warn('[beat-player] fallback idle failed:', err),
      );
    this.scheduleIdleSwap();
    this.emitState();
  }

  /**
   * Pick the next idle variant. Avoids playing the same clip twice in a row
   * (even after the ping-pong reverses, the silhouette is the same so it
   * reads as a repeat). Falls back to defaultIdleUrl if the pool is empty.
   */
  private pickNextIdleUrl(): string {
    if (this.idlePool.length === 0) return this.defaultIdleUrl;
    if (this.idlePool.length === 1) {
      this.idlePoolIdx = 0;
      return this.idlePool[0]!;
    }
    let idx = Math.floor(Math.random() * this.idlePool.length);
    if (idx === this.idlePoolIdx) {
      idx = (idx + 1) % this.idlePool.length;
    }
    this.idlePoolIdx = idx;
    return this.idlePool[idx]!;
  }

  /**
   * Schedule a crossfade to a different idle variant 6–12 s from now.
   * Re-arms after each swap until either the queue receives beats (and
   * `playBeat` clears the timer) or `stop()` is called.
   */
  private scheduleIdleSwap(): void {
    this.clearIdleSwapTimer();
    const swapInMs =
      (BeatPlayer.IDLE_MIN_SEC +
        Math.random() *
          (BeatPlayer.IDLE_MAX_SEC - BeatPlayer.IDLE_MIN_SEC)) *
      1000;
    this.idleSwapTimer = setTimeout(() => {
      // Only swap if we are still in the fallback state — i.e. there is no
      // active beat. A beat will have taken over and we shouldn't tread on it.
      if (!this.active || this.current !== null) return;
      this.playFallbackIdle();
    }, swapInMs);
  }

  private clearIdleSwapTimer(): void {
    if (this.idleSwapTimer !== null) {
      clearTimeout(this.idleSwapTimer);
      this.idleSwapTimer = null;
    }
  }

  /**
   * Resolve every active hold for the incoming beat. Three things happen:
   *
   *   1. `beat.release_holds` are dropped from `activeHolds` and faded
   *      out on the figure (envelope ramps weight → 0 over the beat's
   *      `hold_release_sec`, defaulting to the figure's ~0.5 s).
   *   2. `beat.holds` are added to `activeHolds` (or have their base
   *      weight refreshed if already active). Limb-conflict resolution
   *      auto-releases any older hold that claims the same arm chain
   *      so the new pose can take over.
   *   3. Every entry in `activeHolds` is re-issued to the figure with
   *      its base weight unchanged — no energy-driven attenuation.
   *      Holds are postural ("arms crossed" is binary); if a director
   *      wants the pose to come off in a panicked moment, they emit
   *      `release_holds`. Energy stays a pure motion knob (time-scale,
   *      eye scheduler, HUD).
   */
  private applyHoldsForBeat(beat: Beat): void {
    // The easing family for this beat's activations / releases is keyed
    // on the beat's emotion + intensity (see emotion-tempo.ts). Computed
    // once per applyHoldsForBeat call rather than per-hold so the same
    // beat always uses one curve family.
    const beatEasing: EasingFamily = tempoForEmotion(
      beat.emotion as Emotion,
      beat.intensity ?? 1.0,
    ).easing;

    if (beat.release_holds) {
      // beat.hold_release_sec is the per-beat override; passing
      // undefined falls through to the figure's default (~0.5s).
      // Easing family for the release is the RELEASING beat's emotion —
      // a hold attacked under sadness can release under anger if the
      // dismissing beat is angry.
      const releaseFade = beat.hold_release_sec;
      for (const name of beat.release_holds) {
        this.figure.releaseHold(name, releaseFade, beatEasing);
        this.activeHolds.delete(name);
      }
    }

    if (beat.holds) {
      for (const slot of beat.holds) {
        // Preserve the easing family from the FIRST activation of the
        // hold; subsequent re-issuances (every beat while the hold is
        // sustaining) must not change the family or the figure would
        // re-tag the entry mid-flight.
        const existing = this.activeHolds.get(slot.name);
        this.activeHolds.set(slot.name, {
          url: slot.url,
          baseWeight: slot.weight ?? 0.5,
          attackSec: slot.attack_sec,
          easing: existing?.easing ?? beatEasing,
        });
      }
    }

    // ── Limb conflict dedup ─────────────────────────────────────────────
    //
    // The validator dedups what arrives on a single beat, but the
    // activeHolds map is long-lived and a stale conflicting hold can
    // outlive its scene. If two holds in activeHolds share a limb, the
    // figure would thrash between them every beat.
    //
    // Resolution policy: NEWEST hold wins. We iterate activeHolds in
    // REVERSE insertion order so the most-recently-emitted hold gets
    // first claim on each limb; older conflicting holds are released.
    //
    // Why newer-wins: the LLM's mental model is "emit a new hold to
    // swap." If we kept the older hold and dropped the new one, the
    // character would freeze in the original pose forever once a hold
    // was active — every subsequent attempt to vary the posture would
    // be silently rejected. Newer-wins makes "transition to a new
    // hold" work without the LLM having to explicitly release the old
    // one (though release_holds is still respected when emitted, and
    // the prompt encourages it for clean fades).
    const claimed = new Map<LimbGroup, string>();
    const toRelease: string[] = [];
    const keys = [...this.activeHolds.keys()];
    for (let i = keys.length - 1; i >= 0; i--) {
      const name = keys[i]!;
      const limbs = holdLimbs(name);
      if (limbs.length === 0) continue; // unknown — let it through
      const conflict = limbs.find((l) => claimed.has(l));
      if (conflict) {
        const winner = claimed.get(conflict)!;
        console.log(
          `[beat-player] hold swap: "${name}" (older) released — limb "${conflict}" reassigned to newer "${winner}"`,
        );
        toRelease.push(name);
        continue;
      }
      for (const l of limbs) claimed.set(l, name);
    }
    for (const name of toRelease) {
      this.figure.releaseHold(name);
      this.activeHolds.delete(name);
    }

    if (this.activeHolds.size === 0) return;

    // No energy-based attenuation. Holds are postural — "arms crossed"
    // is binary regardless of how kinetic the surrounding beat feels.
    // If the director wants the pose to come off during a panicked
    // moment, they emit `release_holds`; energy stays a pure
    // motion/timing knob (time-scale, eye scheduler, HUD).
    for (const [name, { url, baseWeight, attackSec, easing }] of this.activeHolds.entries()) {
      // attackSec / easing are forwarded only on the first activation
      // tick — once a hold is already running, re-applying setHold each
      // beat is a no-op for the envelope (figure.setHold ignores both
      // fields on already-running holds and just refreshes the weight).
      this.figure
        .setHold(name, url, baseWeight, attackSec, easing)
        .catch((err) =>
          console.warn(`[beat-player] setHold "${name}" failed:`, err),
        );
    }
  }

  /**
   * Pick a crossfade duration based on what was playing and what is about
   * to play. Two cases:
   *
   *   • Locomotion → locomotion. Both clips share a gait phase (left/right
   *     alternation) and we want the cycle to continue uninterrupted. A
   *     short fade keeps the body's footing visually continuous.
   *   • Anything else (pose → pose, idle → pose, pose → idle, etc.).
   *     The two clips' rest poses are unrelated, so the fade has to do
   *     real work. A longer fade gives the body time to settle into the
   *     destination pose without a visible "snap-to-A-pose-and-back".
   *
   * `previous` is null on the very first beat; in that case we use the
   * pose duration so the fade-in from the figure's bind pose is gentle.
   */
  private static chooseFadeSec(previous: Beat | null, next: Beat): number {
    // Per-beat override wins.
    if (typeof next.fade_sec === 'number' && Number.isFinite(next.fade_sec)) {
      return next.fade_sec;
    }
    const bothLoco =
      previous?.source === 'uthana_locomotion' &&
      next.source === 'uthana_locomotion';
    // Locomotion → locomotion: short fade to keep footwork continuous.
    if (bothLoco) return LOCO_CROSSFADE_SEC;
    // Locomotion → anything else: the character is coming off a running stride
    // (arms pumping, legs fully extended). A standard 1.1 s pose crossfade is
    // too short to settle the body — the weights don't sum to 1.0 during the
    // blend, causing visible bone-position overshoot (flicker). 2.0 s gives
    // enough time for the sprite to naturalistically decelerate into the new
    // pose without any weight-normalisation artefacts.
    if (previous?.source === 'uthana_locomotion') return 2.0;
    return POSE_CROSSFADE_SEC;
  }

  private applyMirror(beat: Beat): void {
    const shouldMirror = beat.symmetric && Math.random() < MIRROR_PROB;
    if (shouldMirror !== this.mirrored) {
      this.mirrored = shouldMirror;
      // Flip X scale on the figure root. Negating X mirrors the whole
      // skeleton without any extra bone work. Works cleanly in wireframe
      // mode; in textured mode normals look correct because Three.js
      // auto-flips winding order on negative-scale meshes.
      this.figure.root.scale.x = Math.abs(this.figure.root.scale.x) * (shouldMirror ? -1 : 1);
    }
  }

  private resetMirror(): void {
    if (this.mirrored) {
      this.mirrored = false;
      this.figure.root.scale.x = Math.abs(this.figure.root.scale.x);
    }
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private notifyQueueDepth(): void {
    const threshold = this.callbacks.queueLowThreshold ?? 1;
    if (this.queue.length <= threshold) {
      this.callbacks.onQueueLow?.(this.queue.length);
    }
    this.emitState();
  }

  private emitState(): void {
    this.callbacks.onStateChange?.(this.state);
  }
}
